import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Button,
    CircularProgress,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import c3crmLogo from "../../../assests/crm_logo.png";
import storyConnect from "../../../assests/login/connect.jpg";
import storyCommunicate from "../../../assests/login/communicate.jpg";
import storyFollowUp from "../../../assests/login/follow-up.jpg";
import storyConvert from "../../../assests/login/convert.jpg";
import c3Hub2 from "../../../assests/login/c3-hub-2.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { loginThunk } from "../authThunk";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "../authHelpers";
import { APP_ROUTES } from "../../../config/appRoutes";
import { departmentThunk, branchDepartmentThunk, branchThunk } from '../../../components/general/generalThunk';
import { getOrganizationsThunk, getTagsThunk } from "../../contact/contactThunk";
import { getContactFilters } from "../../messages/messagesThunk";

// Brand palette, taken from the C3 CRM logo
const NAVY = "#13254F";
const BLUE = "#1677D2";
const ORANGE = "#F28C1B";
const GREEN = "#4CAF2A";
const TEXT = "#0F1B3D";
const MUTED = "#5B6785";
const LINE = "#E4E9F2";
const SOFT_BG = "#F5F8FD";

const tint = {
    [BLUE]: "#E8F2FC",
    [ORANGE]: "#FEF1E2",
    [GREEN]: "#ECF7E7",
    [NAVY]: "#E7EAF2",
};

const livePulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(74,222,128,0.65); }
    70% { box-shadow: 0 0 0 8px rgba(74,222,128,0); }
    100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
`;

const orbitSpin = keyframes`
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
`;

// Connect · Communicate · Convert — the customer journey, works for any business
const STORY = [
    {
        img: storyConnect,
        icon: <HubRoundedIcon />,
        word: "Connect",
        color: BLUE,
        title: "Customers reach you in one scan",
        text: "Place WhatsApp QR codes in your shop, flyers or website. Every scan becomes a saved contact.",
    },
    {
        img: storyCommunicate,
        icon: <ChatRoundedIcon />,
        word: "Communicate",
        color: ORANGE,
        title: "Every inquiry lands in one inbox",
        text: "Your team replies from a shared inbox while chat bots answer common questions 24/7.",
    },
    {
        img: storyFollowUp,
        icon: <EventAvailableRoundedIcon />,
        word: "Follow up",
        color: NAVY,
        title: "Nurture leads with the right tools",
        text: "Assign leads, send quotations, book appointments and reach out with template campaigns.",
    },
    {
        img: storyConvert,
        icon: <TrendingUpRoundedIcon />,
        word: "Convert",
        color: GREEN,
        title: "Turn conversations into customers",
        text: "Close the deal and keep the relationship going, all on the app your customers already use.",
    },
];

// Same module descriptions shown on the dashboard cards
const FEATURES = [
    {
        icon: <ForumRoundedIcon />,
        color: BLUE,
        title: "Messages",
        points: ["Shared inbox for your whole team", "Send images, media and documents", "Reply to customers in real time"],
        short: "Real-time chats in one shared team inbox.",
        desc: "Engage customers through real-time WhatsApp messaging, enabling instant, personal, and reliable communication that builds stronger business relationships.",
    },
    {
        icon: <QrCode2RoundedIcon />,
        color: ORANGE,
        title: "QR Generation",
        points: ["Create custom WhatsApp QR codes", "Use them on print, posters and online", "Every scan becomes a new lead"],
        short: "QR codes that turn every scan into a lead.",
        desc: "Create and manage WhatsApp QR codes to simplify customer interactions, track leads, and boost engagement across print and digital campaigns.",
    },
    {
        icon: <DescriptionRoundedIcon />,
        color: GREEN,
        title: "Templates",
        points: ["Use Meta-approved WhatsApp templates", "Add text, media and quick-reply buttons", "Reuse templates across campaigns"],
        short: "Approved templates for fast replies.",
        desc: "Create and manage WhatsApp message templates to streamline automated and outbound customer communication.",
    },
    {
        icon: <ContactsRoundedIcon />,
        color: NAVY,
        title: "Contacts",
        points: ["One central list of all customers", "Organise with tags and smart filters", "Import contacts in bulk from Excel"],
        short: "All customer contacts in one place.",
        desc: "Centralize and manage all customer contacts efficiently, saving time and improving communication accuracy across your business campaigns.",
    },
    {
        icon: <SmartToyRoundedIcon />,
        color: BLUE,
        title: "Chat Bot",
        points: ["Build flows with a visual builder", "Auto-reply to customers 24/7", "Hand over chats to your team anytime"],
        short: "Instant 24/7 automated answers.",
        desc: "Automate customer interactions with AI-powered WhatsApp chatbots that provide instant responses, 24/7 support, and seamless user experiences.",
    },
    {
        icon: <PeopleAltRoundedIcon />,
        color: GREEN,
        title: "Lead Management",
        points: ["Assign leads to the right staff", "Manage my tasks and team tasks", "Track every follow-up until closed"],
        short: "Assign, track and follow up every lead.",
        desc: "Capture, assign and track every lead, with personal and team task lists so no follow-up is missed.",
    },
    {
        icon: <BadgeRoundedIcon />,
        color: NAVY,
        title: "Employees",
        points: ["Add team members with their own login", "Organise by branch and department", "Control access to each module"],
        short: "Your team, branches and access.",
        desc: "Add team members, organise them by branch and department, and control which modules each person can use.",
    },
    {
        icon: <BusinessRoundedIcon />,
        color: BLUE,
        title: "Organization",
        points: ["Create a profile for each company", "Collect details with custom forms", "Link contacts to their organization"],
        short: "Group contacts by company.",
        desc: "Group contacts under the organizations they belong to and collect their details with custom forms.",
    },
    {
        icon: <RequestQuoteRoundedIcon />,
        color: GREEN,
        title: "Quotation",
        points: ["Professional quotations", "Share on WhatsApp", "Track every quote"],
        short: "Create and share quotations in a few clicks.",
        desc: "Create professional quotations and share them with customers in a few clicks.",
    },
    {
        icon: <CampaignRoundedIcon />,
        color: ORANGE,
        title: "OutBound Campaign",
        points: ["Send bulk WhatsApp campaigns", "Personalise every message by name", "Track delivery and campaign reports"],
        short: "Personalised bulk campaigns that convert.",
        desc: "Reach potential customers through personalized outbound WhatsApp campaigns that nurture leads, boost engagement, and drive measurable conversions.",
    },
];

const SIGNIN_POINTS = [
    { icon: <BadgeRoundedIcon />, label: "Role-based access", color: BLUE },
    { icon: <ForumRoundedIcon />, label: "Team collaboration", color: ORANGE },
    { icon: <WhatsAppIcon />, label: "WhatsApp Cloud API", color: GREEN },
];

// Core features grouped by the logo tagline
const FEATURE_GROUPS = [
    {
        name: "Connect",
        tagline: "Bring customers to you",
        icon: <HubRoundedIcon />,
        color: BLUE,
        gradient: `linear-gradient(135deg, ${BLUE} 0%, #0F5BB5 100%)`,
        items: ["QR Generation", "Contacts", "Organization"],
    },
    {
        name: "Communicate",
        tagline: "Talk on WhatsApp at scale",
        icon: <ChatRoundedIcon />,
        color: ORANGE,
        gradient: `linear-gradient(135deg, ${ORANGE} 0%, #F5A524 100%)`,
        items: ["Messages", "Templates", "Chat Bot", "OutBound Campaign"],
    },
    {
        name: "Convert & grow",
        tagline: "Turn chats into business",
        icon: <TrendingUpRoundedIcon />,
        color: GREEN,
        gradient: `linear-gradient(135deg, ${GREEN} 0%, #2E8B1E 100%)`,
        items: ["Lead Management", "Employees"],
    },
];

// Feature cards in the order a business uses C3 CRM: set up → connect → communicate → convert
const PROCESS_ORDER = [
    "Employees",
    "Contacts",
    "Organization",
    "QR Generation",
    "Templates",
    "Messages",
    "Chat Bot",
    "OutBound Campaign",
    "Lead Management",
];

const JOURNEY_BADGES = [
    { icon: <QrCode2RoundedIcon />, label: "Scan", color: BLUE },
    { icon: <WhatsAppIcon />, label: "Chat", color: ORANGE },
    { icon: <TrendingUpRoundedIcon />, label: "Customer", color: GREEN },
];

const HERO_TAGS = [
    { icon: <ForumRoundedIcon />, label: "Team Inbox" },
    { icon: <CampaignRoundedIcon />, label: "Campaigns" },
    { icon: <SmartToyRoundedIcon />, label: "Chat Bot" },
    { icon: <PeopleAltRoundedIcon />, label: "Leads" },
    { icon: <RequestQuoteRoundedIcon />, label: "Quotations" },
    { icon: <EventAvailableRoundedIcon />, label: "Appointments" },
];

// Custom login input: label, leading icon, native <input> and optional trailing action.
// The native input receives exactly the props the old TextField did (value, handlers, limits).
const LoginField = ({ id, label, icon, error, end, inputProps, sx }) => (
    <Box sx={sx}>
        <Box component="label" htmlFor={id} sx={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#334155", mb: 0.6 }}>
            {label}
        </Box>
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                height: 46,
                px: 1.5,
                borderRadius: "11px",
                backgroundColor: "#fff",
                border: `1.5px solid ${error ? "var(--error)" : "#DCE3EE"}`,
                transition: "border-color .2s, box-shadow .2s",
                "&:hover": { borderColor: error ? "var(--error)" : "#B8C3D6" },
                "&:focus-within": {
                    borderColor: error ? "var(--error)" : BLUE,
                    boxShadow: error ? "0 0 0 4px rgba(239,68,68,0.12)" : "0 0 0 4px rgba(22,119,210,0.12)",
                },
                "& > svg, & .lf-icon svg": { fontSize: 19, color: "#8C97AE", transition: "color .2s" },
                "&:focus-within .lf-icon svg": { color: error ? "var(--error)" : BLUE },
            }}
        >
            <Box className="lf-icon" sx={{ display: "flex" }}>{icon}</Box>
            <Box
                component="input"
                id={id}
                aria-invalid={!!error}
                {...inputProps}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: "100%",
                    border: 0,
                    outline: 0,
                    p: 0,
                    background: "transparent",
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 500,
                    color: TEXT,
                    "&::placeholder": { color: "#9AA5B8", fontWeight: 400 },
                    // Field is white, so autofill paints white too: no mismatched box
                    "&:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 100px #fff inset",
                        WebkitTextFillColor: TEXT,
                        caretColor: TEXT,
                    },
                }}
            />
            {end}
        </Box>
        {error && (
            <Box sx={{ mt: 0.5, ml: 0.25, fontSize: 12, color: "var(--error)" }} role="alert">
                {error}
            </Box>
        )}
    </Box>
);

const sectionWrap = { maxWidth: 1200, mx: "auto" };

// The logo PNG has wide transparent margins; crop to the mark itself (width: px number or CSS length)
const Logo = ({ width = 168 }) => (
    <Box sx={{ width, aspectRatio: "1 / 0.362", overflow: "hidden", position: "relative", flexShrink: 0 }}>
        <Box
            component="img"
            src={c3crmLogo}
            alt="C3 CRM - Connect Communicate Convert"
            sx={{ position: "absolute", maxWidth: "none", width: "147.2%", left: "-23.6%", top: "-64.6%" }}
        />
    </Box>
);

// Floating "back to top" button: appears after scrolling down, smooth-scrolls to the top on click
const ScrollTopButton = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 400);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <Box
            component="button"
            type="button"
            aria-label="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{
                position: "fixed",
                right: { xs: 16, md: 28 },
                bottom: { xs: 16, md: 28 },
                zIndex: 1300,
                width: { xs: 44, md: 50 },
                height: { xs: 44, md: 50 },
                p: 0,
                border: "2px solid rgba(255,255,255,0.9)",
                borderRadius: "50%",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                background: `linear-gradient(135deg, ${BLUE} 0%, ${NAVY} 100%)`,
                boxShadow: "0 12px 28px -10px rgba(19,37,79,0.7)",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.85)",
                pointerEvents: visible ? "auto" : "none",
                transition: "opacity .25s, transform .25s, box-shadow .2s",
                "&:hover": { boxShadow: "0 16px 32px -10px rgba(22,119,210,0.8)", transform: "translateY(-3px) scale(1)" },
                "&:focus-visible": { outline: `3px solid ${ORANGE}`, outlineOffset: 2 },
                "& svg": { fontSize: { xs: 26, md: 30 } },
            }}
        >
            <KeyboardArrowUpRoundedIcon />
        </Box>
    );
};

const Auth = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [state, setState] = useState({
        userName: "",
        password: "",
        showPassword: false,
        errors: {},
    });

    // ✅ single snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const { showPassword, userName, password, errors } = state;
    const { status, error } = useSelector((state) => state.authentication);

    const handleClickShowPassword = () => {
        setState((prevState) => ({
            ...prevState,
            showPassword: !prevState.showPassword,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({
            ...prev,
            [name]: value,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleKeyPress = (e) => {
        if (e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
        } else {
            if (e.key === "Enter") handleLogIn(e);
        }
    };

    const handleLogIn = async (e) => {
        e.preventDefault();
        const validations = [
            {
                valid: !!userName,
                message: "Username is required",
                field: "userName",
            },
            {
                valid: !!password,
                message: "Password is required",
                field: "password",
            },
            {
                valid: password.length >= 6,
                message: "Password must be at least 6 characters",
                field: "password",
            },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar({
                    open: true,
                    message: rule.message,
                    severity: "error",
                });
                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));
                return;
            }
        }

        try {
            const result = await dispatch(
                loginThunk({ credentials: { username: userName, password } })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                // Run these independent data-fetching thunks in parallel.
                // Use Promise.allSettled so that a failure in one doesn't prevent navigation.
                try {
                    const fetchPromises = [
                        dispatch(departmentThunk()),
                        dispatch(branchDepartmentThunk()),
                        dispatch(branchThunk()),
                        dispatch(getOrganizationsThunk()),
                        dispatch(getTagsThunk()),
                        dispatch(getContactFilters()),
                    ];

                    const settled = await Promise.allSettled(fetchPromises);
                    const failed = settled.filter((s) => s.status === "rejected" || (s.status === 'fulfilled' && s.value && s.value.error));
                    if (failed.length) {
                        // Non-blocking warning to user; still navigate to dashboard
                        setSnackbar({
                            open: true,
                            message: "Some background data failed to load. You may need to refresh.",
                            severity: "warning",
                        });
                    }
                } catch (err) {
                    // Fallback: if Promise.allSettled itself throws (unlikely), notify user
                    setSnackbar({
                        open: true,
                        message: "Background data failed to load. You may need to refresh.",
                        severity: "warning",
                    });
                }

                navigate(APP_ROUTES.DASHBOARD);
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (window.location.pathname === APP_ROUTES.LOGIN) {
            const isAuthenticatedUser = isAuthenticated();
            if (isAuthenticatedUser) {
                navigate(APP_ROUTES.DASHBOARD);
            }
        }
    }, [navigate]);

    useEffect(() => {
        if (error !== null) {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [error]);

    const isLoading = status === "loading";

    const scrollToStory = () => {
        document.getElementById("c3-story")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <Box sx={{ backgroundColor: "#fff", color: TEXT }}>
            {/* ───────── Hero: brand blue panel + sign-in (first screen) ───────── */}
            <Box
                component="section"
                sx={{
                    // At least one screen tall; grows instead of overlapping on short screens / high zoom
                    minHeight: "100vh",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.1fr) minmax(0, 1fr)" },
                    backgroundColor: "#fff",
                }}
            >
                {/* Left: brand panel */}
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        flexDirection: "column",
                        // Logo, headline and steps grouped and centred vertically, evenly spaced
                        justifyContent: "center",
                        gap: { md: 2.25, lg: 3 },
                        position: "relative",
                        overflow: "hidden",
                        color: "#fff",
                        // Always as tall as the section (no gap underneath), at least one screen
                        minHeight: { md: "100vh" },
                        px: { md: 6, lg: 8 },
                        py: { md: 2.5, lg: 3.5 },
                        background: `linear-gradient(150deg, ${BLUE} 0%, #1456A8 45%, ${NAVY} 100%)`,
                    }}
                >
                    {/* Soft decorative circles */}
                    <Box aria-hidden sx={{ position: "absolute", width: 460, height: 460, borderRadius: "50%", right: -170, top: -170, border: "1px solid rgba(255,255,255,0.12)" }} />
                    <Box aria-hidden sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", right: -90, top: -90, border: "1px solid rgba(255,255,255,0.12)" }} />
                    <Box aria-hidden sx={{ position: "absolute", width: 560, height: 560, borderRadius: "50%", right: -200, bottom: -260, background: "radial-gradient(circle, rgba(242,140,27,0.30), transparent 65%)" }} />

                    {/* Faint WhatsApp watermark, centred inside the top-right outline circles */}
                    <WhatsAppIcon
                        aria-hidden
                        sx={{ position: "absolute", right: -30, top: -30, fontSize: 180, color: "rgba(255,255,255,0.07)", transform: "rotate(-12deg)" }}
                    />

                    {/* Dot pattern (top-left) */}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: 260,
                            height: 200,
                            backgroundImage: "radial-gradient(rgba(255,255,255,0.22) 1.5px, transparent 1.5px)",
                            backgroundSize: "18px 18px",
                            maskImage: "linear-gradient(135deg, #000 20%, transparent 75%)",
                            WebkitMaskImage: "linear-gradient(135deg, #000 20%, transparent 75%)",
                        }}
                    />

                    {/* Logo + live badge */}
                    <Box
                        sx={{
                            "@media (min-width: 2000px)": { zoom: 1.25 },
                            position: "relative",
                            alignSelf: "flex-start",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                            p: 0.75,
                            pr: 1,
                            borderRadius: "18px",
                            // Frosted-glass header bar
                            backgroundColor: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            backdropFilter: "blur(10px)",
                            boxShadow: "0 14px 34px -18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
                        }}
                    >
                        <Box sx={{ backgroundColor: "#fff", borderRadius: "12px", px: 1.25, py: 0.75, display: "inline-flex", boxShadow: "0 10px 30px -14px rgba(0,0,0,0.45)" }}>
                            <Logo width={120} />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 0.6,
                                borderRadius: "999px",
                                backgroundColor: "rgba(255,255,255,0.12)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4ADE80", animation: `${livePulse} 2s infinite` }} />
                            Live on WhatsApp Cloud API
                        </Box>
                    </Box>

                    {/* Headline */}
                    <Box sx={{ position: "relative", maxWidth: 620, "@media (min-width: 2000px)": { zoom: 1.25 } }}>
                        {/* Tagline as three pills */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                            {[
                                { label: "Connect", color: "#8FD3FF" },
                                { label: "Communicate", color: "#FFC46B" },
                                { label: "Convert", color: "#9BE38A" },
                            ].map((t) => (
                                <Box
                                    key={t.label}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.45,
                                        borderRadius: "999px",
                                        backgroundColor: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.16)",
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: t.color,
                                    }}
                                >
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: t.color, boxShadow: `0 0 8px ${t.color}` }} />
                                    {t.label}
                                </Box>
                            ))}
                        </Box>

                        {/* Main heading on a single line (keeps the first screen from scrolling) */}
                        <Typography
                            component="h1"
                            sx={{
                                fontSize: { md: "clamp(22px, min(4.4vh, 2.5vw), 32px)", lg: "clamp(26px, min(4.8vh, 2.5vw), 44px)" },
                                fontWeight: 800,
                                lineHeight: 1.15,
                                letterSpacing: "-0.02em",
                                whiteSpace: { lg: "nowrap" },
                                mb: 1,
                            }}
                        >
                            Turn{" "}
                            <Box component="span" sx={{ color: "#4ADE80" }}>
                                WhatsApp
                            </Box>{" "}
                            chats into customers.
                        </Typography>

                        <Typography sx={{ fontSize: { md: 14, lg: 15 }, lineHeight: 1.6, color: "rgba(255,255,255,0.78)", maxWidth: 560 }}>
                            Manage contacts, reply from a shared inbox, automate with chat bots and close more
                            deals, all in one place.
                        </Typography>
                    </Box>

                    {/* C3 CRM hub: Connect, Communicate, Campaigns, Chat Bot, Leads, Analytics */}
                    <Box
                        sx={{
                            position: "relative",
                            // Centred in the panel so there is no empty corner; capped by screen height to fit one screen
                            width: { xs: "100%", md: "min(100%, 760px, calc(53vh * 1.374))", xl: "min(100%, 980px, calc(50vh * 1.374))" },
                            aspectRatio: "1470 / 1070",
                            alignSelf: "center",
                            animation: `${float} 7s ease-in-out infinite`,
                        }}
                    >
                        {/* Halo */}
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                inset: "6%",
                                borderRadius: "50%",
                                background: "radial-gradient(circle, rgba(255,255,255,0.26), rgba(108,184,255,0.16) 45%, transparent 70%)",
                                filter: "blur(26px)",
                            }}
                        />
                        {/* Slowly rotating orbit rings centred on the hub */}
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                width: "78%",
                                aspectRatio: "1 / 1",
                                transform: "translate(-50%, -50%)",
                                borderRadius: "50%",
                                border: "2px dashed rgba(255,255,255,0.22)",
                                animation: `${orbitSpin} 40s linear infinite`,
                            }}
                        />
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                width: "96%",
                                aspectRatio: "1 / 1",
                                transform: "translate(-50%, -50%)",
                                borderRadius: "50%",
                                border: "1px solid rgba(255,255,255,0.10)",
                            }}
                        />
                        <Box
                            component="img"
                            src={c3Hub2}
                            alt="C3 CRM: connect, communicate, run campaigns, chat bot, lead management, analyze and grow"
                            sx={{
                                position: "relative",
                                display: "block",
                                width: "100%",
                                height: "100%",
                                maxWidth: "none",
                                objectFit: "contain",
                                filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.35))",
                            }}
                        />
                    </Box>

                    {/* Trust line + module tags (hidden on short screens so the steps keep their room) */}
                    <Box sx={{ position: "relative", maxWidth: 620, "@media (max-height: 860px)": { display: "none" }, "@media (min-width: 2000px)": { zoom: 1.25 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
                            <WhatsAppIcon sx={{ fontSize: 18, color: "#4ADE80" }} />
                            Built on the WhatsApp Business Platform
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {HERO_TAGS.map((t) => (
                                <Box
                                    key={t.label}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.6,
                                        px: 1.1,
                                        py: 0.5,
                                        borderRadius: "999px",
                                        backgroundColor: "rgba(255,255,255,0.10)",
                                        border: "1px solid rgba(255,255,255,0.16)",
                                        fontSize: 12,
                                        fontWeight: 500,
                                        "& svg": { fontSize: 14, color: "rgba(255,255,255,0.85)" },
                                    }}
                                >
                                    {t.icon}
                                    {t.label}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Right: sign-in */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        px: { xs: 2, sm: 5 },
                        py: { xs: 4, md: 6 },
                        backgroundColor: SOFT_BG,
                        backgroundImage: "radial-gradient(rgba(19,37,79,0.06) 1px, transparent 1px)",
                        backgroundSize: "20px 20px",
                    }}
                >
                    {/* Soft brand-colour glows behind the card */}
                    <Box aria-hidden sx={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", top: "8%", right: "-6%", background: "radial-gradient(circle, rgba(22,119,210,0.16), transparent 65%)" }} />
                    <Box aria-hidden sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", bottom: "4%", left: "-4%", background: "radial-gradient(circle, rgba(76,175,42,0.14), transparent 65%)" }} />
                    <Box aria-hidden sx={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", bottom: "30%", right: "10%", background: "radial-gradient(circle, rgba(242,140,27,0.12), transparent 65%)" }} />

                    <Box sx={{ position: "relative", width: "100%", maxWidth: 380, "@media (min-width: 2000px)": { zoom: 1.25 } }}>
                    {/* Dotted accents peeking out behind the card corners */}
                    <Box
                        aria-hidden
                        sx={{
                            display: { xs: "none", md: "block" },
                            position: "absolute",
                            top: -22,
                            left: -22,
                            width: 110,
                            height: 110,
                            backgroundImage: `radial-gradient(${BLUE} 1.6px, transparent 1.6px)`,
                            backgroundSize: "14px 14px",
                            opacity: 0.35,
                        }}
                    />
                    <Box
                        aria-hidden
                        sx={{
                            display: { xs: "none", md: "block" },
                            position: "absolute",
                            bottom: -22,
                            right: -22,
                            width: 110,
                            height: 110,
                            backgroundImage: `radial-gradient(${ORANGE} 1.6px, transparent 1.6px)`,
                            backgroundSize: "14px 14px",
                            opacity: 0.4,
                        }}
                    />
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            backgroundColor: "#fff",
                            borderRadius: "20px",
                            border: `1px solid ${LINE}`,
                            boxShadow: "0 30px 60px -32px rgba(19,37,79,0.35)",
                            p: { xs: 2.5, sm: 3.5 },
                            // Brand accent strip along the top edge
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: -1,
                                left: 24,
                                right: 24,
                                height: 4,
                                borderRadius: "0 0 6px 6px",
                                background: `linear-gradient(90deg, ${BLUE} 0 33%, ${ORANGE} 33% 66%, ${GREEN} 66% 100%)`,
                            },
                        }}
                    >


                        {/* Logo on small screens, where the brand panel is hidden */}
                        <Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
                            <Logo width={150} />
                        </Box>
                        <Typography component="h2" sx={{ fontSize: 22, fontWeight: 800, color: NAVY, letterSpacing: "-0.02em", mb: 0.25 }}>
                            Welcome back
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, color: MUTED, mb: 2.5 }}>
                            Sign in to continue to your C3 CRM workspace.
                        </Typography>

                        {/* Username */}
                        <LoginField
                            id="login-username"
                            label="User Name"
                            icon={<PersonOutlineRoundedIcon />}
                            error={errors.userName}
                            sx={{ mb: 1.75 }}
                            inputProps={{
                                name: "userName",
                                type: "text",
                                placeholder: "Enter your user name",
                                value: userName,
                                onChange: handleChange,
                                onKeyDown: handleKeyPress,
                                maxLength: 16,
                                autoComplete: "username",
                            }}
                        />

                        {/* Password */}
                        <LoginField
                            id="login-password"
                            label="Password"
                            icon={<LockOutlinedIcon />}
                            error={errors.password}
                            sx={{ mb: 2.25 }}
                            inputProps={{
                                name: "password",
                                type: showPassword ? "text" : "password",
                                placeholder: "Enter your password",
                                value: password,
                                onChange: handleChange,
                                onKeyDown: handleKeyPress,
                                minLength: 6,
                                maxLength: 16,
                                autoComplete: "current-password",
                            }}
                            end={
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    size="small"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    sx={{ color: "#8C97AE", "&:hover": { color: BLUE } }}
                                >
                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            }
                        />

                        <Button
                            fullWidth
                            disableElevation
                            variant="contained"
                            onClick={isLoading ? null : handleLogIn}
                            disabled={isLoading}
                            endIcon={isLoading ? null : <ArrowForwardRoundedIcon />}
                            sx={{
                                height: 46,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: 15,
                                fontWeight: 700,
                                color: "#fff",
                                background: `linear-gradient(135deg, ${BLUE} 0%, #0F5BB5 100%)`,
                                boxShadow: "0 14px 28px -12px rgba(22,119,210,0.75)",
                                transition: "box-shadow .2s, transform .15s, filter .2s",
                                "&:hover": { filter: "brightness(1.06)", transform: "translateY(-1px)", boxShadow: "0 18px 32px -12px rgba(22,119,210,0.85)" },
                                "&:active": { transform: "translateY(0)" },
                                "& .MuiButton-endIcon": { transition: "transform .2s" },
                                "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
                                "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
                            }}
                        >
                            {isLoading && <CircularProgress size={18} sx={{ color: "#fff", mr: 1.25 }} />}
                            {isLoading ? "Signing in..." : "Sign In"}
                        </Button>

                        {/* Secure sign-in divider + help */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5, mb: 1 }}>
                            <Box sx={{ flex: 1, height: "1px", backgroundColor: LINE }} />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12, fontWeight: 600, color: GREEN }}>
                                <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
                                Secure sign-in
                            </Box>
                            <Box sx={{ flex: 1, height: "1px", backgroundColor: LINE }} />
                        </Box>
                        <Typography sx={{ textAlign: "center", fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
                            Trouble logging in? Contact your organization administrator.
                        </Typography>
                    </Box>
                    </Box>

                    {/* Feature points under the card (hidden on short screens) */}
                    <Box
                        sx={{
                            position: "relative",
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: { xs: 1.5, sm: 2.5 },
                            mt: 4,
                            "@media (max-height: 760px)": { display: "none" },
                        }}
                    >
                        {SIGNIN_POINTS.map((p) => (
                            <Box key={p.label} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12.5, fontWeight: 600, color: "#46536F" }}>
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "8px",
                                        display: "grid",
                                        placeItems: "center",
                                        backgroundColor: "#fff",
                                        boxShadow: "0 4px 10px -4px rgba(19,37,79,0.3)",
                                        "& svg": { fontSize: 15, color: p.color },
                                    }}
                                >
                                    {p.icon}
                                </Box>
                                {p.label}
                            </Box>
                        ))}
                    </Box>

                    {/* Scroll cue */}
                    <Box
                        component="button"
                        type="button"
                        onClick={scrollToStory}
                        sx={{
                            mt: 3,
                            border: 0,
                            background: "none",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            color: MUTED,
                            fontSize: 12.5,
                            fontWeight: 600,
                            fontFamily: "inherit",
                            "&:hover": { color: BLUE },
                        }}
                    >
                        Explore C3 CRM
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 22, animation: `${float} 2.2s ease-in-out infinite` }} />
                    </Box>
                </Box>
            </Box>

            {/* ───────── How it works: illustrated journey ───────── */}
            <Box id="c3-story" component="section" sx={{ px: 2, pt: { xs: 8, md: 11 }, pb: { xs: 9, md: 12 }, position: "relative", overflow: "hidden", background: `radial-gradient(700px 400px at 8% 0%, rgba(22,119,210,0.10), transparent 70%), radial-gradient(600px 380px at 95% 100%, rgba(76,175,42,0.10), transparent 70%), radial-gradient(500px 300px at 60% 40%, rgba(242,140,27,0.06), transparent 70%), ${SOFT_BG}` }}>
                {/* Soft grid fading out behind the heading */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 460,
                        backgroundImage: `linear-gradient(rgba(19,37,79,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(19,37,79,0.06) 1px, transparent 1px)`,
                        backgroundSize: "48px 48px",
                        maskImage: "radial-gradient(ellipse 55% 75% at 50% 0%, #000 30%, transparent 100%)",
                        WebkitMaskImage: "radial-gradient(ellipse 55% 75% at 50% 0%, #000 30%, transparent 100%)",
                    }}
                />
                {/* Blurred brand glows behind the cards */}
                <Box aria-hidden sx={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", left: "8%", top: "48%", background: `${BLUE}`, opacity: 0.12, filter: "blur(90px)" }} />
                <Box aria-hidden sx={{ position: "absolute", width: 380, height: 380, borderRadius: "50%", left: "42%", top: "55%", background: `${ORANGE}`, opacity: 0.10, filter: "blur(90px)" }} />
                <Box aria-hidden sx={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", right: "6%", top: "46%", background: `${GREEN}`, opacity: 0.12, filter: "blur(90px)" }} />
                {/* Wave flowing into the next (white) section */}
                <Box
                    component="svg"
                    aria-hidden
                    viewBox="0 0 1440 120"
                    preserveAspectRatio="none"
                    sx={{ position: "absolute", left: 0, right: 0, bottom: -1, width: "100%", height: { xs: 50, md: 90 }, display: "block", zIndex: 1 }}
                >
                    <defs>
                        {/* Matches the dark Core features section below (blue glow on the left, navy on the right) */}
                        <linearGradient id="c3WaveFill" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#13254F" />
                            <stop offset="100%" stopColor="#11204A" />
                        </linearGradient>
                    </defs>
                    <path d="M0,64 C240,120 480,8 720,40 C960,72 1200,124 1440,56 L1440,120 L0,120 Z" fill="url(#c3WaveFill)" />
                </Box>

                {/* Decorations: dotted patches and outline rings */}
                <Box aria-hidden sx={{ position: "absolute", top: 40, left: 40, width: 150, height: 110, backgroundImage: `radial-gradient(${BLUE} 1.6px, transparent 1.6px)`, backgroundSize: "16px 16px", opacity: 0.25 }} />
                <Box aria-hidden sx={{ position: "absolute", bottom: 60, right: 40, width: 150, height: 110, backgroundImage: `radial-gradient(${ORANGE} 1.6px, transparent 1.6px)`, backgroundSize: "16px 16px", opacity: 0.3 }} />
                <Box aria-hidden sx={{ position: "absolute", top: -120, right: -120, width: 360, height: 360, borderRadius: "50%", border: `2px dashed ${tint[BLUE]}` }} />
                <Box aria-hidden sx={{ position: "absolute", bottom: -140, left: -100, width: 320, height: 320, borderRadius: "50%", border: `2px solid ${tint[GREEN]}` }} />
                <Box sx={{ ...sectionWrap, position: "relative" }}>
                    {/* Section heading */}
                    <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: { xs: 5, md: 6 } }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.75,
                                py: 0.6,
                                mb: 2,
                                borderRadius: "999px",
                                backgroundColor: "#fff",
                                border: `1px solid ${LINE}`,
                                boxShadow: "0 6px 16px -10px rgba(19,37,79,0.35)",
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: BLUE,
                            }}
                        >
                            <Box sx={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: tint[BLUE], "& svg": { fontSize: 13 } }}>
                                <RocketLaunchRoundedIcon />
                            </Box>
                            How C3 CRM works
                        </Box>
                        <Typography
                            component="h2"
                            sx={{ fontSize: { xs: 28, md: 42 }, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15, color: NAVY, mb: 2 }}
                        >
                            From first scan to{" "}
                            <Box component="span" sx={{ position: "relative", display: "inline-block", whiteSpace: "nowrap" }}>
                                <Box
                                    component="span"
                                    sx={{
                                        background: `linear-gradient(90deg, ${BLUE} 0%, ${GREEN} 100%)`,
                                        WebkitBackgroundClip: "text",
                                        backgroundClip: "text",
                                        color: "transparent",
                                    }}
                                >
                                    loyal customer
                                </Box>
                                {/* Brush underline */}
                                <Box
                                    component="svg"
                                    viewBox="0 0 300 16"
                                    preserveAspectRatio="none"
                                    aria-hidden
                                    sx={{ position: "absolute", left: 0, right: 0, bottom: { xs: -8, md: -12 }, width: "100%", height: { xs: 10, md: 14 } }}
                                >
                                    <path d="M3 11 C 70 3, 150 2, 297 8" fill="none" stroke={ORANGE} strokeWidth="5" strokeLinecap="round" />
                                </Box>
                            </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 15.5, lineHeight: 1.6, color: MUTED, mt: 2.5 }}>
                            Shops, clinics, schools, real estate, service providers: any business can connect, communicate
                            and convert on WhatsApp.
                        </Typography>

                        {/* Mini journey: Scan → Chat → Customer (smaller screens; on large screens these sit on the progress line) */}
                        <Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", justifyContent: "center", flexWrap: "nowrap", gap: { xs: 0.5, sm: 1 }, mt: 3 }}>
                            {JOURNEY_BADGES.map((c, i) => (
                                <Box key={c.label} sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
                                    {i > 0 && <ArrowForwardRoundedIcon sx={{ fontSize: { xs: 14, sm: 18 }, color: "#A7B1C6" }} />}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: { xs: 0.5, sm: 0.75 },
                                            pl: 0.5,
                                            pr: { xs: 1, sm: 1.5 },
                                            py: 0.5,
                                            borderRadius: "999px",
                                            backgroundColor: "#fff",
                                            border: `1px solid ${LINE}`,
                                            whiteSpace: "nowrap",
                                            fontSize: { xs: 12, sm: 13 },
                                            fontWeight: 600,
                                            color: NAVY,
                                        }}
                                    >
                                        <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: c.color, "& svg": { fontSize: 14, color: "#fff" } }}>
                                            {c.icon}
                                        </Box>
                                        {c.label}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    {/* Journey strip: progress line with 4 stops, then 4 glass step cards */}
                    <Box sx={{ position: "relative" }}>
                        {/* Progress line (large screens) */}
                        <Box sx={{ display: { xs: "none", lg: "grid" }, gridTemplateColumns: "repeat(4, 1fr)", gap: 3, mb: 3.5, position: "relative" }}>
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "12.5%",
                                    right: "12.5%",
                                    height: 3,
                                    transform: "translateY(-50%)",
                                    borderRadius: 3,
                                    background: `linear-gradient(90deg, ${BLUE}, ${ORANGE}, ${NAVY}, ${GREEN})`,
                                    opacity: 0.35,
                                }}
                            />
                            {STORY.map((s, i) => (
                                <Box key={s.word} sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            backgroundColor: s.color,
                                            color: "#fff",
                                            fontWeight: 800,
                                            fontSize: 16,
                                            boxShadow: `0 0 0 6px ${tint[s.color]}, 0 8px 18px -6px ${s.color}`,
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                </Box>
                            ))}

                            {/* Badges sitting on the line between the stops: 1–2 Scan, 2–3 Chat, 3–4 Customer */}
                            {JOURNEY_BADGES.map((c, i) => (
                                <Box
                                    key={c.label}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        // midpoints between the 4 grid columns (gap = 24px)
                                        left: ["calc(25% - 6px)", "50%", "calc(75% + 6px)"][i],
                                        transform: "translate(-50%, -50%)",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        pl: 0.6,
                                        pr: 1.5,
                                        py: 0.5,
                                        borderRadius: "999px",
                                        backgroundColor: "#fff",
                                        border: `1px solid ${LINE}`,
                                        boxShadow: "0 8px 18px -10px rgba(19,37,79,0.4)",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: NAVY,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: c.color, "& svg": { fontSize: 14, color: "#fff" } }}>
                                        {c.icon}
                                    </Box>
                                    {c.label}
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 3 }}>
                            {STORY.map((s, i) => {
                                const accent = s.color;
                                return (
                                    <Box
                                        key={s.word}
                                        sx={{
                                            position: "relative",
                                            p: 1.5,
                                            pb: 3,
                                            borderRadius: "22px",
                                            backgroundColor: "#fff",
                                            border: `1px solid ${LINE}`,
                                            boxShadow: "0 18px 40px -28px rgba(19,37,79,0.45)",
                                            transition: "transform .25s, box-shadow .25s, border-color .25s",
                                            "&:hover": {
                                                transform: "translateY(-6px)",
                                                borderColor: accent,
                                                boxShadow: "0 26px 48px -28px rgba(19,37,79,0.55)",
                                            },
                                            // Arrow connector to the next card (large screens)
                                            ...(i < STORY.length - 1 && {
                                                "&::after": {
                                                    content: '"›"',
                                                    display: { xs: "none", lg: "grid" },
                                                    placeItems: "center",
                                                    position: "absolute",
                                                    top: "34%",
                                                    right: -21,
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: "50%",
                                                    backgroundColor: NAVY,
                                                    color: "#fff",
                                                    fontSize: 16,
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                    zIndex: 1,
                                                },
                                            }),
                                        }}
                                    >
                                        <Box sx={{ position: "relative", borderRadius: "16px", overflow: "hidden", mb: 2.5 }}>
                                            <Box
                                                component="img"
                                                src={s.img}
                                                alt={`${s.word}: ${s.title}`}
                                                loading="lazy"
                                                sx={{ display: "block", width: "100%", aspectRatio: "4 / 3", objectFit: "cover" }}
                                            />
                                            <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 4, backgroundColor: accent }} />
                                        </Box>

                                        <Box sx={{ px: 1 }}>
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    mb: 1.25,
                                                    color: accent,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    letterSpacing: "0.1em",
                                                    textTransform: "uppercase",
                                                    "& svg": { fontSize: 16 },
                                                }}
                                            >
                                                {s.icon}
                                                {s.word}
                                            </Box>
                                            <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY, lineHeight: 1.3, mb: 1 }}>
                                                {s.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: MUTED }}>{s.text}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                </Box>
            </Box>

            {/* ───────── Core features ───────── */}
            <Box id="c3-features" component="section" sx={{ px: 2, pt: { xs: 4, md: 5 }, pb: { xs: 8, md: 11 }, position: "relative", overflow: "hidden", background: `radial-gradient(700px 420px at 10% 38%, rgba(22,119,210,0.35), transparent 70%), radial-gradient(600px 400px at 95% 100%, rgba(76,175,42,0.20), transparent 70%), radial-gradient(500px 320px at 55% 50%, rgba(242,140,27,0.10), transparent 70%), linear-gradient(160deg, ${NAVY} 0%, #0B1838 100%)` }}>
                {/* Faint grid texture */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                        maskImage: "radial-gradient(ellipse at 50% 20%, #000 20%, transparent 75%)",
                        WebkitMaskImage: "radial-gradient(ellipse at 50% 20%, #000 20%, transparent 75%)",
                    }}
                />
                <Box sx={{ ...sectionWrap, position: "relative" }}>
                    {/* Heading + group legend */}
                    <Box sx={{ mb: { xs: 5, md: 6 } }}>
                    <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto" }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.75,
                                py: 0.6,
                                mb: 2,
                                borderRadius: "999px",
                                backgroundColor: "#fff",
                                border: `1px solid ${LINE}`,
                                boxShadow: "0 6px 16px -10px rgba(0,0,0,0.5)",
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: BLUE,
                            }}
                        >
                            <Box sx={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: tint[BLUE], "& svg": { fontSize: 13 } }}>
                                <AutoAwesomeRoundedIcon />
                            </Box>
                            Core features
                        </Box>
                        <Typography component="h2" sx={{ fontSize: { xs: 28, md: 38, lg: 44 }, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15, color: "#fff", mb: 1.5 }}>
                            Everything your business needs on{" "}
                            <Box
                                component="span"
                                sx={{
                                    background: "linear-gradient(90deg, #4ADE80 0%, #6CB8FF 100%)",
                                    WebkitBackgroundClip: "text",
                                    backgroundClip: "text",
                                    color: "transparent",
                                }}
                            >
                                WhatsApp
                            </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 15.5, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>
                            One platform to message, automate and grow, built on the official WhatsApp Business Platform.
                        </Typography>
                    </Box>

                    {/* Group legend */}
                    <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1.25, mt: 3 }}>
                        {FEATURE_GROUPS.map((g) => (
                            <Box
                                key={g.name}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.6,
                                    borderRadius: "999px",
                                    backgroundColor: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.14)",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.9)",
                                }}
                            >
                                <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: g.color, boxShadow: `0 0 10px ${g.color}` }} />
                                {g.name}
                            </Box>
                        ))}
                    </Box>
                    </Box>

                    {/* Feature tiles */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                            gap: 2,
                        }}
                    >
                        {PROCESS_ORDER.map((title, idx) => ({ idx, g: FEATURE_GROUPS.find((x) => x.items.includes(title)), f: FEATURES.find((x) => x.title === title) })).map(({ idx, g, f }) => (
                            <Box
                                key={f.title}
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    p: 2.25,
                                    borderRadius: "18px",
                                    height: "100%",
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    backdropFilter: "blur(8px)",
                                    transition: "transform .25s, border-color .25s, background-color .25s, box-shadow .25s",
                                    // coloured top edge
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 3,
                                        background: `linear-gradient(90deg, ${g.color}, transparent)`,
                                    },
                                    "&:hover": {
                                        transform: "translateY(-6px)",
                                        borderColor: g.color,
                                        backgroundColor: "rgba(255,255,255,0.08)",
                                        boxShadow: `0 24px 40px -24px ${g.color}`,
                                    },
                                    "&:hover .tile-glow": { opacity: 0.55 },
                                }}
                            >
                                {/* soft glow behind the icon */}
                                <Box
                                    aria-hidden
                                    className="tile-glow"
                                    sx={{
                                        position: "absolute",
                                        width: 140,
                                        height: 140,
                                        borderRadius: "50%",
                                        left: -40,
                                        top: -50,
                                        backgroundColor: g.color,
                                        filter: "blur(50px)",
                                        opacity: 0.3,
                                        transition: "opacity .25s",
                                    }}
                                />
                                {/* Process step number */}
                                <Typography
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        top: 14,
                                        right: 18,
                                        fontSize: 28,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        color: "rgba(255,255,255,0.10)",
                                    }}
                                >
                                    {String(idx + 1).padStart(2, "0")}
                                </Typography>
                                <Box sx={{ position: "relative" }}>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 4 }}>
                                        <Box
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                flexShrink: 0,
                                                borderRadius: "12px",
                                                display: "grid",
                                                placeItems: "center",
                                                background: g.gradient,
                                                boxShadow: `0 10px 22px -10px ${g.color}`,
                                                "& svg": { fontSize: 22, color: "#fff" },
                                            }}
                                        >
                                            {f.icon}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#fff", lineHeight: 1.3, mb: 0.25 }}>{f.title}</Typography>
                                            <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "rgba(255,255,255,0.72)" }}>{f.short}</Typography>
                                        </Box>
                                    </Box>
                                    {/* Key points */}
                                    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 1.5, pt: 1.25, borderTop: "1px dashed rgba(255,255,255,0.14)", display: "flex", flexDirection: "column", gap: 0.75 }}>
                                        {f.points.map((p) => (
                                            <Box component="li" key={p} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13.5, lineHeight: 1.45, fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>
                                                <Box sx={{ width: 18, height: 18, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: g.color, boxShadow: `0 0 8px ${g.color}`, "& svg": { fontSize: 12, color: "#fff" } }}>
                                                    <CheckRoundedIcon />
                                                </Box>
                                                {p}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ───────── Footer ───────── */}
            <Box
                component="footer"
                sx={{
                    py: 1.75,
                    px: 2,
                    textAlign: "center",
                    backgroundColor: "#0B1838",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.6)",
                    fontSize: 12.5,
                }}
            >
                © {new Date().getFullYear()} C3 CRM. All rights reserved.
            </Box>

            {/* Back to top */}
            <ScrollTopButton />

            {/* Snackbar Message */}
            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </Box>
    );
};

export default Auth;
